import React, { useState, useEffect } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { useAuth } from '../hooks/useAuth';
import { 
  Formation, 
  FormationType, 
  GroupType, 
  PlayerPosition,
  fieldConfigurations,
  getFormations,
  getPlayersPerTeam,
  validateLineup
} from '../utils/formations';
import { 
  Loader2, 
  AlertCircle, 
  Save, 
  Trophy, 
  Check,
  RefreshCw,
  Calendar
} from 'lucide-react';
import FieldVisualizer from '../components/lineup/FieldVisualizer';
import PlayerList from '../components/lineup/PlayerList';
import SavedLineups from '../components/lineup/SavedLineups';

// Helper function to get today's date in YYYY-MM-DD format (local time)
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface Player {
  id: string;
  nome: string;
  posicao: PlayerPosition;
  nivel: number;
}

interface LineupPlayer extends Player {
  posicao_campo: string;
  x: number;
  y: number;
}

interface SavedLineup {
  id: string;
  data_escalacao: string;
  tipo_formacao: FormationType;
  formacao_nome: string;
  jogadores_titulares: LineupPlayer[];
  jogadores_reservas: string[];
  criado_em: string;
}

const LineupPage: React.FC = () => {
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const { activeGroup } = useActiveGroup();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [groupType, setGroupType] = useState<GroupType | null>(null);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [titularPlayers, setTitularPlayers] = useState<LineupPlayer[]>([]);
  const [reservePlayers, setReservePlayers] = useState<Player[]>([]);

  const [selectedFormationType, setSelectedFormationType] = useState<FormationType>('neutra');
  const [selectedFormation, setSelectedFormation] = useState<Formation | null>(null);
  const [availableFormations, setAvailableFormations] = useState<Formation[]>([]);
  const [lineupDate, setLineupDate] = useState(getTodayDateString());
  const [savedLineups, setSavedLineups] = useState<SavedLineup[]>([]);

  // Calcular jogadores disponíveis dinamicamente
  const availablePlayers = React.useMemo(() => {
    const titularIds = new Set(titularPlayers.map(p => p.id));
    const reserveIds = new Set(reservePlayers.map(p => p.id));
    return allPlayers.filter(p => !titularIds.has(p.id) && !reserveIds.has(p.id));
  }, [allPlayers, titularPlayers, reservePlayers]);

  const fetchData = async () => {
    if (!activeGroup) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      
      // Fetch group type
      const { data: groupData, error: groupError } = await supabase
        .from('grupos')
        .select('tipo')
        .eq('id', activeGroup.id)
        .single();

      if (groupError) throw groupError;
      setGroupType(groupData.tipo as GroupType);

      // Fetch all players for the active group
      const { data: playersData, error: playersError } = await supabase
        .from('jogadores')
        .select('id, nome, posicao, nivel')
        .eq('grupo_id', activeGroup.id)
        .eq('ativo', true)
        .order('nome');

      if (playersError) throw playersError;
      setAllPlayers(playersData as Player[]);

      // Fetch saved lineups for the group
      const { data: lineupsData, error: lineupsError } = await supabase
        .from('escalacoes')
        .select('*')
        .eq('grupo_id', activeGroup.id)
        .order('data_escalacao', { ascending: false });

      if (lineupsError) throw lineupsError;
      setSavedLineups(lineupsData as SavedLineup[]);

    } catch (err: any) {
      console.error('Error fetching data for lineup page:', err);
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeGroup, supabase]);

  useEffect(() => {
    if (groupType) {
      const formations = getFormations(groupType, selectedFormationType);
      setAvailableFormations(formations);
      if (formations.length > 0) {
        setSelectedFormation(formations[0]);
      } else {
        setSelectedFormation(null);
      }
    }
  }, [groupType, selectedFormationType]);

  const handleDragStart = (e: React.DragEvent, player: Player) => {
    e.dataTransfer.setData('player', JSON.stringify(player));
  };

  const handleDropOnField = (fieldPositionId: string, player: Player) => {
    // Check if position is already occupied
    const existingPlayerInPosition = titularPlayers.find(p => p.posicao_campo === fieldPositionId);
    
    if (existingPlayerInPosition) {
      // Move existing player to reserves
      const existingPlayer = allPlayers.find(p => p.id === existingPlayerInPosition.id);
      if (existingPlayer) {
        setReservePlayers(prev => [...prev, existingPlayer]);
      }
      // Remove from titular
      setTitularPlayers(prev => prev.filter(p => p.posicao_campo !== fieldPositionId));
    }

    // Remove from reserves if it was there
    setReservePlayers(prev => prev.filter(p => p.id !== player.id));

    // Add new player to titular
    const fieldPosition = selectedFormation?.fieldPositions.find(fp => fp.id === fieldPositionId);
    setTitularPlayers(prev => [...prev, {
      ...player,
      posicao_campo: fieldPositionId,
      x: fieldPosition?.x || 0,
      y: fieldPosition?.y || 0
    }]);

    // Auto-move remaining players to reserves when team is complete
    const newTitularCount = titularPlayers.length + 1;
    const playersPerTeam = getPlayersPerTeam(groupType!);
    
    if (newTitularCount === playersPerTeam) {
      // Move all remaining available players to reserves
      const titularIds = new Set([...titularPlayers.map(p => p.id), player.id]);
      const reserveIds = new Set(reservePlayers.map(p => p.id));
      const remainingAvailable = allPlayers.filter(p => !titularIds.has(p.id) && !reserveIds.has(p.id));
      setReservePlayers(prev => [...prev, ...remainingAvailable]);
    }
  };

  const handleDropOnReserve = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const playerData = e.dataTransfer.getData('player');
      if (playerData) {
        const player = JSON.parse(playerData);
        
        // Remove from titular players
        setTitularPlayers(prev => prev.filter(p => p.id !== player.id));
        // Add to reserves
        setReservePlayers(prev => [...prev, player]);
      }
    } catch (error) {
      console.error('Error handling drop on reserve:', error);
    }
  };

  const handleRemovePlayer = (playerId: string) => {
    // Remove from titular
    setTitularPlayers(prev => prev.filter(p => p.id !== playerId));
    // Remove from reserves
    setReservePlayers(prev => prev.filter(p => p.id !== playerId));
  };

  const handleSaveLineup = async () => {
    if (!activeGroup || !user || !selectedFormation || !groupType) {
      setError('Dados incompletos para salvar a escalação.');
      return;
    }

    // Validate lineup
    const validation = validateLineup(titularPlayers, groupType, selectedFormation);
    if (!validation.isValid) {
      setError(validation.errors.join('. '));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: insertError } = await supabase
        .from('escalacoes')
        .insert({
          grupo_id: activeGroup.id,
          data_escalacao: `${lineupDate} 12:00:00`, // Adiciona o meio-dia para evitar problemas de fuso horário
          tipo_formacao: selectedFormationType,
          formacao_nome: selectedFormation.name,
          jogadores_titulares: titularPlayers,
          jogadores_reservas: reservePlayers.map(p => p.id),
          criado_por: user.id,
        });

      if (insertError) throw insertError;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Refresh apenas as escalações salvas, mantendo o estado atual dos jogadores
      const { data: lineupsData, error: lineupsError } = await supabase
        .from('escalacoes')
        .select('*')
        .eq('grupo_id', activeGroup.id)
        .order('data_escalacao', { ascending: false });

      if (!lineupsError) {
        setSavedLineups(lineupsData as SavedLineup[]);
      }

    } catch (err: any) {
      console.error('Error saving lineup:', err);
      setError('Erro ao salvar escalação: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadLineup = (lineup: SavedLineup) => {
    // Clear current state
    setTitularPlayers([]);
    setReservePlayers([]);

    // Set formation details
    // Extrair apenas a parte da data e garantir formato correto
    const dateOnly = lineup.data_escalacao.split(' ')[0]; // "YYYY-MM-DD"
    setLineupDate(dateOnly);
    setSelectedFormationType(lineup.tipo_formacao);
    
    // Find and set formation
    const formations = getFormations(groupType!, lineup.tipo_formacao);
    const formation = formations.find(f => f.name === lineup.formacao_nome);
    setSelectedFormation(formation || null);

    // Populate titular players
    setTitularPlayers(lineup.jogadores_titulares);

    // Populate reserve players
    const reserves = allPlayers.filter(p => lineup.jogadores_reservas.includes(p.id));
    setReservePlayers(reserves);
  };

  const handleDeleteLineup = async (lineupId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta escalação?')) return;

    setDeleting(lineupId);
    try {
      const { error } = await supabase
        .from('escalacoes')
        .delete()
        .eq('id', lineupId);

      if (error) throw error;

      // Refresh saved lineups
      setSavedLineups(prev => prev.filter(lineup => lineup.id !== lineupId));
    } catch (err: any) {
      console.error('Error deleting lineup:', err);
      setError('Erro ao excluir escalação: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicateLineup = (lineup: SavedLineup) => {
    handleLoadLineup(lineup);
    setLineupDate(getTodayDateString());
  };

  const clearLineup = () => {
    setTitularPlayers([]);
    setReservePlayers([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2">Carregando...</span>
      </div>
    );
  }

  if (!activeGroup) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para gerenciar escalações
        </p>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Escalação</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Monte a escalação do seu time para o jogo
        </p>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <p>Escalação salva com sucesso!</p>
        </div>
      )}

      {/* Layout Mobile */}
      <div className="block lg:hidden space-y-6">
        {/* Controles Mobile */}
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Configurações</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="lineupDate" className="block text-sm font-medium mb-1">
                Data da Escalação
              </label>
              <input
                type="date"
                id="lineupDate"
                className="input w-full"
                value={lineupDate}
                onChange={(e) => setLineupDate(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="formationType" className="block text-sm font-medium mb-1">
                Tipo de Formação
              </label>
              <select
                id="formationType"
                className="input w-full"
                value={selectedFormationType}
               onChange={(e) => {
                 setSelectedFormationType(e.target.value as FormationType);
                 clearLineup();
               }}
              >
                <option value="neutra">Neutra</option>
                <option value="ofensiva">Ofensiva</option>
                <option value="defensiva">Defensiva</option>
              </select>
            </div>

            <div>
              <label htmlFor="formationName" className="block text-sm font-medium mb-1">
                Formação Tática
              </label>
              <select
                id="formationName"
                className="input w-full"
                value={selectedFormation?.name || ''}
                onChange={(e) => {
                  const formation = availableFormations.find(f => f.name === e.target.value);
                  setSelectedFormation(formation || null);
                  clearLineup();
                }}
                disabled={availableFormations.length === 0}
              >
                <option value="">Selecione uma formação</option>
                {availableFormations.map(f => (
                  <option key={f.name} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </select>
              {selectedFormation && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedFormation.description}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* Campo Mobile */}
        <FieldVisualizer
          formation={selectedFormation}
          titularPlayers={titularPlayers}
          availablePlayers={availablePlayers}
          onDropPlayer={handleDropOnField}
          onRemovePlayer={handleRemovePlayer}
          onSave={handleSaveLineup}
          onClear={clearLineup}
          saving={saving}
        />

        {/* Jogadores e Reservas Mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlayerList
            title="Jogadores Disponíveis"
            players={availablePlayers}
            onDragStart={handleDragStart}
            emptyMessage="Todos os jogadores estão escalados"
          />

          <PlayerList
            title="Banco de Reservas"
            players={reservePlayers}
            onDragStart={handleDragStart}
            emptyMessage="Arraste jogadores para o banco"
            showDropZone={true}
            onDrop={handleDropOnReserve}
          />
        </div>
 
        {/* Escalações Salvas Mobile */}
        <SavedLineups
          lineups={savedLineups}
          onLoadLineup={handleLoadLineup}
          onDeleteLineup={handleDeleteLineup}
          onDuplicateLineup={handleDuplicateLineup}
          deleting={deleting}
        />
      </div>
 
      {/* Layout Desktop */}
      <div className="hidden lg:block space-y-6">
        {/* Configurações Horizontais */}
        <div className="card p-6">
          <h2 className="text-lg font-bold mb-4">Configurações</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="lineupDateDesktop" className="block text-sm font-medium mb-1">
                Data da Escalação
              </label>
              <input
                type="date"
                id="lineupDateDesktop"
                className="input w-full"
                value={lineupDate}
                onChange={(e) => setLineupDate(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="formationTypeDesktop" className="block text-sm font-medium mb-1">
                Tipo de Formação
              </label>
              <select
                id="formationTypeDesktop"
                className="input w-full"
                value={selectedFormationType}
               onChange={(e) => {
                 setSelectedFormationType(e.target.value as FormationType);
                 clearLineup();
               }}
              >
                <option value="neutra">Neutra</option>
                <option value="ofensiva">Ofensiva</option>
                <option value="defensiva">Defensiva</option>
              </select>
            </div>

            <div>
              <label htmlFor="formationNameDesktop" className="block text-sm font-medium mb-1">
                Formação Tática
              </label>
              <select
                id="formationNameDesktop"
                className="input w-full"
                value={selectedFormation?.name || ''}
                onChange={(e) => {
                  const formation = availableFormations.find(f => f.name === e.target.value);
                  setSelectedFormation(formation || null);
                  clearLineup();
                }}
                disabled={availableFormations.length === 0}
              >
                <option value="">Selecione uma formação</option>
                {availableFormations.map(f => (
                  <option key={f.name} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </select>
              {selectedFormation && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedFormation.description}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSaveLineup}
                className="btn-primary flex-1 h-10"
                disabled={saving || !selectedFormation || !groupType}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar
                  </>
                )}
              </button>

              <button
                onClick={clearLineup}
                className="btn-outline h-10"
                title="Limpar escalação"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Campo de Futebol */}
        <FieldVisualizer
          formation={selectedFormation}
          titularPlayers={titularPlayers}
          availablePlayers={availablePlayers}
          onDropPlayer={handleDropOnField}
          onRemovePlayer={handleRemovePlayer}
          onSave={handleSaveLineup}
          onClear={clearLineup}
          saving={saving}
        />

        {/* Jogadores e Banco de Reservas lado a lado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlayerList
            title="Jogadores Disponíveis"
            players={availablePlayers}
            onDragStart={handleDragStart}
            emptyMessage="Todos os jogadores estão escalados"
          />

          <PlayerList
            title="Banco de Reservas"
            players={reservePlayers}
            onDragStart={handleDragStart}
            emptyMessage="Arraste jogadores para o banco"
            showDropZone={true}
            onDrop={handleDropOnReserve}
          />
        </div>

        {/* Escalações Salvas */}
        <SavedLineups
          lineups={savedLineups}
          onLoadLineup={handleLoadLineup}
          onDeleteLineup={handleDeleteLineup}
          onDuplicateLineup={handleDuplicateLineup}
          deleting={deleting}
        />
      </div>
    </div>
  );
};

export default LineupPage;